<template>
	<PageLoading
		:loading="inventory.loading"
		class="relative mt-4"
		:class="{ 'pb-28': selectedItems.length }"
	>
		<div class="flex gap-5 px-4">
			<PageTitle class="px-0!">{{ t('backpack') }}</PageTitle>
			<RouterLink to="/market" custom v-slot="{ navigate }">
				<PageTitle
					@click="navigate"
					class="px-0! opacity-30"
					v-hover="{ active: 'scale-95' }"
				>
					{{ t('market') }}
				</PageTitle>
			</RouterLink>
		</div>
		<PagePillRow class="mt-4 *:mx-0">
			<SearchButton v-model="search" />
			<BaseButton
				color="secondary"
				size="md"
				@click="toggleSort"
				class="snap-start"
			>
				<svg
					class="h-5 w-5 transition-transform"
					width="20"
					height="20"
					viewBox="0 0 20 20"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
					:class="{ 'rotate-180': sortType === 'cheap' }"
				>
					<path
						d="M2.5 4.16667H17.5"
						stroke="currentColor"
						stroke-width="2"
						stroke-linecap="round"
					/>
					<path
						:d="
							sortType === 'recent' ? 'M2.5 15.8333H7.5' : 'M7.5 15.8333H12.5'
						"
						stroke="currentColor"
						stroke-width="2"
						stroke-linecap="round"
						class="transition-[d]"
					/>
					<path
						:d="sortType === 'recent' ? 'M2.5 10H12.5' : 'M5 10H15'"
						stroke="currentColor"
						stroke-width="2"
						stroke-linecap="round"
						class="transition-[d]"
					/>
				</svg>
				<div>{{ sortText }}</div>
			</BaseButton>
			<div class="bg-separator-secondary mx-2 h-4 w-px"></div>
			<BaseButton
				color="secondary"
				size="md"
				class="snap-start"
				@click="selectAll"
			>
				<BaseCheckbox v-model="allSelected" class="h-5! w-5!" />
				<div>{{ t('selectAll') }}</div>
			</BaseButton>
		</PagePillRow>
		<PagePadding class="mt-3 space-y-1.5" v-if="inventory.items.length">
			<InventoryItem
				v-for="item in itemsSorted"
				:src="icon(item.modelUuid)"
				:collection="item.collectionName"
				:name="item.modelName"
				:price="item.price"
				:preloader="item.preloader"
				:claiming="item.claiming"
				:error="item.error"
				@click="toggleSelect(item.uuid, item.claiming, item.error)"
				v-hover="{ active: 'scale-95' }"
				@retry="retry(item)"
			>
				<BaseCheckbox :modelValue="selectedItems.includes(item.uuid)" />
			</InventoryItem>
		</PagePadding>
		<PageEmpty :title="t('empty.title')" :desc="t('empty.desc')" v-else>
			<RouterLink to="/" custom v-slot="{ navigate }">
				<BaseButton size="lg" color="primary" @click="navigate">
					<CaseIcon class="h-5 w-5" />
					<div>{{ t('openCases') }}</div>
				</BaseButton>
			</RouterLink>
		</PageEmpty>
		<Transition
			enterActiveClass="transition-all duration-300 ease-in-out"
			leaveActiveClass="transition-all duration-300 ease-in-out"
			leaveToClass="translate-y-full opacity-0"
			enterFromClass="translate-y-full opacity-0"
		>
			<BottomActions v-if="selectedItems.length">
				<CountAndTotal :count="selectedItems.length" :total="selectedSum" />
				<div class="mt-3 flex gap-1.5">
					<BaseButton
						class="flex-1"
						@click="claim"
						size="lg"
						color="secondary"
						:disabled="selling || nonClaimableSelected"
					>
						<ClaimIcon class="h-5 w-5" />
						<div>{{ allClaimableSelected ? t('claimAll') : t('claim') }}</div>
					</BaseButton>
					<BaseButton
						class="flex-1"
						@click="sell"
						size="lg"
						color="primary"
						:loading="selling"
					>
						<SellIcon class="h-5 w-5" />
						<div>{{ allSelected ? t('sellAll') : t('sell') }}</div>
					</BaseButton>
				</div>
			</BottomActions>
		</Transition>
		<ClaimModal
			v-model="claimModal"
			:items="claimItems"
			:all="allSelected"
			@removeItem="removeItem"
			@claimed="claimed"
		/>
		<ClaimInteractionModal
			v-model="inventory.claimInteractionModal"
			@retry="retryAfterInteraction"
		/>
		<SellFailedModal v-model="sellFailedModal" :items="sellFailedItems" />
	</PageLoading>
</template>
<script setup lang="ts">
import PageTitle from '@/components/PageTitle.vue'
import { useLayout } from '@/stores/layout'
import BaseButton from '@/components/BaseButton.vue'
import BottomActions from '@/components/BottomActions.vue'
import SellIcon from '@/assets/icons/sell.svg'
import ClaimIcon from '@/assets/icons/claim.svg'
import { computed, ref } from 'vue'
import PagePillRow from '@/components/PagePillRow.vue'
import SortIcon from '@/assets/icons/sort.svg'
import BaseCheckbox from '@/components/BaseCheckbox.vue'
import PagePadding from '@/components/PagePadding.vue'
import InventoryItem from '@/components/InventoryItem.vue'
import CaseIcon from '@/assets/icons/case.svg'
import ClaimModal from '@/components/modals/ClaimModal.vue'
import SearchButton from '@/components/SearchButton.vue'
import { icon } from '@/utils/urls'
import PageLoading from '@/components/PageLoading.vue'
import PageEmpty from '@/components/PageEmpty.vue'
import { useInventory } from '@/stores/inventory'
import { useI18n } from 'vue-i18n'
import ClaimInteractionModal from '@/components/modals/ClaimInteractionModal.vue'
import CountAndTotal from '@/components/CountAndTotal.vue'
import SellFailedModal from '@/components/modals/SellFailedModal.vue'
import { useToast } from '@/stores/toast'

const { t } = useI18n({
	messages: {
		en: {
			backpack: 'Backpack',
			market: 'Market',
			selectAll: 'Select all',
			openCases: 'Open cases',
			claim: 'claim',
			claimAll: 'claim all',
			sell: 'sell',
			sellAll: 'sell all',
			sort: {
				expensive: 'Expensive first',
				cheap: 'Cheap first',
				recent: 'Recent first'
			},
			empty: {
				title: `You don't have any gifts`,
				desc: 'Open cases to obtain some'
			},
			failedToSell: 'Failed to sell selected gifts',
			failedToSellSome: 'Failed to sell {n} of the selected gifts'
		},
		ru: {
			backpack: 'Инвентарь',
			market: 'Маркет',
			selectAll: 'Выбрать все',
			openCases: 'Открыть кейсы',
			claim: 'забрать',
			sell: 'продать',
			sellAll: 'продать все',
			claimAll: 'забрать все',
			sort: {
				expensive: 'Сначала дорогие',
				cheap: 'Сначала дешевые',
				recent: 'Сначала новые'
			},
			empty: {
				title: 'У тебя нет гифтов',
				desc: 'Открывай кейсы, чтобы получить'
			},
			failedToSell: 'Не удалось продать выбранные гифты',
			failedToSellSome: 'Не удалось продать {n} из выбранных гифтов'
		}
	}
})

const inventory = useInventory()

const layout = useLayout()

layout.showBottomNavbar()

const selectedItems = ref<string[]>([])

const toggleSelect = (uuid: string, claiming: boolean, error?: string) => {
	if (claiming || error) {
		return
	}

	const index = selectedItems.value.indexOf(uuid)

	if (index > -1) {
		selectedItems.value.splice(index, 1)
	} else {
		selectedItems.value.push(uuid)
	}
}

const removeItem = (uuid: string) => {
	const index = selectedItems.value.indexOf(uuid)

	if (index === -1) {
		return
	}

	selectedItems.value.splice(index, 1)
}

const claimModal = ref(false)

const claim = () => {
	if (!selectedItems.value.length) {
		return
	}
	if (selling.value) {
		return
	}

	claimModal.value = true
}

const sortType = ref<'recent' | 'expensive' | 'cheap'>('recent')

const toggleSort = () => {
	if (sortType.value === 'expensive') {
		sortType.value = 'recent'
	} else if (sortType.value === 'recent') {
		sortType.value = 'cheap'
	} else if (sortType.value === 'cheap') {
		sortType.value = 'expensive'
	}
}

const sortText = computed(() => {
	return t(`sort.${sortType.value}`)
})

const search = ref('')

const itemsSorted = computed(() => {
	const strippedSearch = search.value.toLowerCase().replace(/[^a-zA-Z0-9]/g, '')
	return inventory.items
		.filter(
			(item) =>
				`${item.modelName}${item.collectionName}`
					.toLowerCase()
					.replace(/[^a-zA-Z0-9]/g, '')
					.includes(strippedSearch) ||
				`${item.collectionName}${item.modelName}`
					.toLowerCase()
					.replace(/[^a-zA-Z0-9]/g, '')
					.includes(strippedSearch)
		)
		.slice(0)
		.sort((a, b) => {
			// Prioritize claimable or claiming items at the top
			const aClaimingOrError = a.claiming || a.error
			const bClaimingOrError = b.claiming || b.error

			if (aClaimingOrError && !bClaimingOrError) return -1
			if (!aClaimingOrError && bClaimingOrError) return 1

			if (sortType.value === 'recent') {
				return 0
			}

			// Then sort by price
			return sortType.value === 'expensive'
				? b.price - a.price
				: a.price - b.price
		})
})

const selectedSum = computed(() => {
	return selectedItems.value.reduce((sum, uuid) => {
		const item = inventory.items.find((item) => item.uuid === uuid)
		return sum + (item ? item.price : 0)
	}, 0)
})

const nonClaimableSelected = computed(() => {
	return selectedItems.value.every((uuid) => {
		const item = inventory.items.find((item) => item.uuid === uuid)
		return item && !item.claimable
	})
})

const allSelected = computed(() => {
	return selectedItems.value.length === itemsSorted.value.length
})

const allClaimableSelected = computed(() => {
	return (
		selectedItems.value.every((uuid) => {
			const item = inventory.items.find((item) => item.uuid === uuid)
			return item && item.claimable
		}) && allSelected.value
	)
})

const claimItems = computed(() =>
	inventory.items.filter((item) => selectedItems.value.includes(item.uuid))
)

const selectAll = () => {
	if (allSelected.value) {
		selectedItems.value = []
	} else {
		selectedItems.value = itemsSorted.value
			.filter((item) => !item.claiming && !item.error)
			.map((item) => item.uuid)
	}
}

const selling = ref(false)
const toast = useToast()

const sell = async () => {
	if (!selectedItems.value.length) {
		return
	}
	if (selling.value) {
		return
	}

	selling.value = true

	const soldItems = await inventory.sellItems(selectedItems.value)

	if (!soldItems.length) {
		toast.add({
			type: 'error',
			text: t('failedToSell')
		})
	}
	if (soldItems.length !== selectedItems.value.length) {
		toast.add({
			type: 'error',
			text: t('failedToSellSome', selectedItems.value.length - soldItems.length)
		})
	}

	selectedItems.value = selectedItems.value.filter(
		(uuid) => !soldItems.includes(uuid)
	)

	selling.value = false
}

const sellFailedModal = ref(false)
const sellFailedItems = ref<
	{
		uuid: string
		modelUuid: string
		collectionName: string
		modelName: string
		price: number
		preloader?: string | null
		claiming?: boolean
		error?:
			| 'UNAVAILABLE'
			| 'PRICE_MISMATCH'
			| 'INTERNAL_ERROR'
			| 'BOT_INTERACTION_REQUIRED'
	}[]
>([])

const retry = (item: {
	uuid: string
	modelUuid: string
	collectionName: string
	modelName: string
	price: number
	preloader?: string | null
	claiming?: boolean
	error?:
		| 'UNAVAILABLE'
		| 'PRICE_MISMATCH'
		| 'INTERNAL_ERROR'
		| 'BOT_INTERACTION_REQUIRED'
}) => {
	if (item.claiming && !item.error) {
		return
	}
	if (item.error === 'BOT_INTERACTION_REQUIRED') {
		inventory.openClaimInteractionModal()
		return
	}

	sellFailedItems.value = inventory.items.filter(
		(i) =>
			i.error &&
			['UNAVAILABLE', 'PRICE_MISMATCH', 'INTERNAL_ERROR'].includes(i.error)
	)
	sellFailedModal.value = true
}

const retryAfterInteraction = async () => {
	await inventory.claimItems(
		inventory.items
			.filter(
				(item) =>
					item.error &&
					!item.claiming &&
					item.error === 'BOT_INTERACTION_REQUIRED'
			)
			.map((item) => item.uuid)
	)
}

const claimed = () => {
	selectedItems.value = selectedItems.value.filter((uuid) => {
		const item = inventory.items.find((item) => item.uuid === uuid)
		return item && !item.claimable
	})
}
</script>
